import { ethers } from "ethers";
import { useMemo } from "react";
import ERC20ABI from "../../abis/ERC20.json";
import { getUsdcDecimals, isNativeGasQuoteToken } from "../../config/chains";
import { getTokenAddress as getConfiguredTokenAddress } from "../../config/tokens";
import {
  AllCollaterals,
  DepositWithdrawCollaterals,
  IsUSDC,
  getCollateralERC20Addresses,
} from "../../constants/addresses";
import { getPerpetualDexContracts } from "../../constants/perpetual-dex/addresses";
import { ChainIdEnum } from "../../enums/chain";
import { IWallet } from "../wallet/useWallet";
import { useUnifiedVaultActions } from "./useUnifiedVault.actions";
import {
  BALANCE_MANAGER_VAULT_ABI,
  BalanceManagerABI,
  CLOBEngineDepositABI,
  UnifiedVaultABI,
} from "./useUnifiedVault.constants";
import { useUnifiedVaultReads } from "./useUnifiedVault.reads";

/**
 * Hook for interacting with UnifiedVault / BalanceManager for deposits
 *
 * Three deposit modes:
 * - Monad: CLOBEngine.deposit(currency, amount, sender, user) — consolidated contract
 * - Arc (BalanceManagerVault chains): BalanceManager.deposit(currency, amount, sender, user) — modular
 * - Legacy: UnifiedVault.deposit(amount) — standalone vault
 *
 * On BalanceManagerVault chains, a single deposit to BalanceManager serves both spot and perp.
 * The BalanceManagerVault automatically bridges those funds for perp positions.
 */
export const useUnifiedVault = (
  wallet: IWallet,
  asset: AllCollaterals = DepositWithdrawCollaterals.Usdc,
  decimals: number = getUsdcDecimals()
) => {
  const { account, provider, chainId } = wallet;

  const contracts = useMemo(() => {
    return getPerpetualDexContracts(chainId as ChainIdEnum);
  }, [chainId]);

  const isMonad = chainId === ChainIdEnum.MONAD_TESTNET;
  const nativeIsQuote = useMemo(() => isNativeGasQuoteToken(chainId as ChainIdEnum), [chainId]);

  // Chains with balanceManager use BalanceManager for deposits (like Monad uses CLOBEngine)
  const useBalanceManagerDeposit = !!contracts?.balanceManager;

  // Determine where deposits go:
  // - Monad: CLOBEngine (router)
  // - BalanceManager chains (Arc): BalanceManager
  // - Legacy: UnifiedVault
  const depositTarget = useMemo(() => {
    if (isMonad) return contracts?.router;
    if (useBalanceManagerDeposit) return contracts?.balanceManager;
    return contracts?.unifiedVault;
  }, [isMonad, useBalanceManagerDeposit, contracts]);

  const depositTokenAddress = useMemo(() => {
    if ((isMonad || useBalanceManagerDeposit) && IsUSDC(asset) && contracts?.usdc) {
      return contracts.usdc;
    }

    const addresses = getCollateralERC20Addresses(asset, chainId as ChainIdEnum);
    // Do NOT fall back to contracts.usdc — if the selected token has no address
    // on this chain, operations must fail explicitly rather than silently use USDC.
    return addresses.l2 || addresses.l1 || undefined;
  }, [asset, chainId, contracts?.usdc, isMonad, useBalanceManagerDeposit]);
  const wnativeTokenAddress = useMemo(
    () =>
      chainId
        ? getConfiguredTokenAddress(chainId, "WNATIVE")?.toLowerCase()
        : undefined,
    [chainId]
  );
  const depositTokenIsNative = useMemo(
    () =>
      !!depositTokenAddress &&
      !!wnativeTokenAddress &&
      depositTokenAddress.toLowerCase() === wnativeTokenAddress,
    [depositTokenAddress, wnativeTokenAddress]
  );

  // Contract for executing deposits
  const depositContract = useMemo(() => {
    if (!provider || !depositTarget) return undefined;

    let abi;
    if (isMonad) {
      abi = CLOBEngineDepositABI;
    } else if (useBalanceManagerDeposit) {
      abi = BalanceManagerABI;
    } else {
      abi = UnifiedVaultABI;
    }

    return new ethers.Contract(depositTarget, abi, provider.getSigner());
  }, [provider, depositTarget, isMonad, useBalanceManagerDeposit]);

  // Vault contract for reading balances (BalanceManagerVault or UnifiedVault)
  const vaultContract = useMemo(() => {
    if (!provider || !contracts?.unifiedVault) return undefined;

    const abi = useBalanceManagerDeposit ? BALANCE_MANAGER_VAULT_ABI : UnifiedVaultABI;
    return new ethers.Contract(contracts.unifiedVault, abi, provider.getSigner());
  }, [provider, contracts, useBalanceManagerDeposit]);

  const depositTokenContract = useMemo(() => {
    if (!provider || !depositTokenAddress) return undefined;

    return new ethers.Contract(
      depositTokenAddress,
      ERC20ABI,
      provider.getSigner()
    );
  }, [provider, depositTokenAddress]);
  const usdcContract = depositTokenContract;
  const depositTokenIsNativeQuote = useMemo(
    () =>
      nativeIsQuote &&
      !!depositTokenAddress &&
      depositTokenAddress.toLowerCase() === contracts?.usdc?.toLowerCase(),
    [contracts, depositTokenAddress, nativeIsQuote]
  );

  const { approveUSDC, getAllowance, deposit, withdraw, transferBalance } =
    useUnifiedVaultActions({
      account,
      provider,
      chainId: chainId as ChainIdEnum | undefined,
      contracts,
      isMonad,
      useBalanceManagerDeposit,
      nativeIsQuote,
      depositTarget,
      depositContract,
      vaultContract,
      depositTokenContract,
      depositTokenAddress,
      depositTokenIsNative,
      asset,
      decimals,
    });

  const {
    getAccount,
    getTotalBalance,
    getAvailableBalance,
    getCrossMarginAllocated,
    getIsolatedAllocated,
    getUSDCBalance,
    isApproved,
  } = useUnifiedVaultReads({
    vaultContract,
    account,
    usdcContract,
    provider,
    chainId: chainId as ChainIdEnum | undefined,
    depositTokenIsNative,
    depositTokenIsNativeQuote,
    getAllowance,
  });

  return {
    // Contract instances
    unifiedVaultContract: vaultContract,
    usdcContract,

    // Account info
    account,
    chainId,
    contracts,

    // Functions
    approveUSDC,
    getAllowance,
    deposit,
    withdraw,
    transferBalance,
    getAccount,
    getTotalBalance,
    getAvailableBalance,
    getCrossMarginAllocated,
    getIsolatedAllocated,
    getUSDCBalance,
    isApproved,
    isNativeDepositAsset: depositTokenIsNative,
  };
};
